import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, FlatList, StyleSheet, Alert } from 'react-native';
import { db, auth } from '../firebaseConfig';
import { doc, getDoc, setDoc, updateDoc, arrayUnion, onSnapshot } from 'firebase/firestore';
import { serverTimestamp } from 'firebase/firestore';

const Chat = ({ route }) => {
  const { orderId, companyId, motoboyId } = route.params || {};
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const currentUser = auth.currentUser?.uid;

  // Validações iniciais
  if (!currentUser) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>Erro: Usuário não autenticado.</Text>
      </View>
    );
  }

  if (!orderId || !companyId || !motoboyId) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>Erro: Parâmetros do chat ausentes.</Text>
      </View>
    );
  }

  // Inicialização do chat e carregamento de mensagens
  useEffect(() => {
    const chatRef = doc(db, 'chats', orderId);

    const initializeChat = async () => {
      try {
        const chatDoc = await getDoc(chatRef);
        if (!chatDoc.exists()) {
          await setDoc(chatRef, { messages: [] });
        }
      } catch (error) {
        Alert.alert('Erro', 'Não foi possível carregar o chat.');
      }
    };

    initializeChat();

    const unsubscribe = onSnapshot(chatRef, async (snapshot) => {
      if (snapshot.exists()) {
        const chatData = snapshot.data() || {};
        const newMessages = chatData.messages || [];
        setMessages(newMessages);

        // Marcar mensagens como lidas
        const currentUserId = auth.currentUser?.uid;
        const updatedMessages = newMessages.map((msg) => {
          if (
            msg.senderId !== currentUserId && // Mensagem enviada por outro usuário
            (!msg.readBy || !msg.readBy.includes(currentUserId)) // Mensagem não lida por este usuário
          ) {
            return {
              ...msg,
              readBy: [...(msg.readBy || []), currentUserId],
            };
          }
          return msg;
        });

        // Atualizar mensagens no Firestore se houver alterações
        const hasUnreadMessages = updatedMessages.some(
          (msg, index) => newMessages[index].readBy !== updatedMessages[index].readBy
        );

        if (hasUnreadMessages) {
          try {
            await updateDoc(chatRef, { messages: updatedMessages });
          } catch (error) {
            console.error('Erro ao atualizar mensagens como lidas:', error);
          }
        }
      }
    });

    return () => unsubscribe();
  }, [orderId]);

  // Função para enviar mensagem
  const sendMessage = async () => {
    if (!message.trim()) return;

    const chatRef = doc(db, 'chats', orderId);
    const newMessage = {
      senderId: currentUser,
      message: message.trim(),
      timestamp: serverTimestamp(),
    };

    try {
      await updateDoc(chatRef, {
        messages: arrayUnion({
          ...newMessage,
          timestamp: new Date(), // Adiciona um timestamp local para exibição imediata
        }),
      });
      setMessage(''); // Limpa o campo de entrada
    } catch (error) {
      Alert.alert('Erro ao enviar mensagem.');
    }
  };

  return (
    <View style={styles.container}>
      <FlatList
        data={[...messages].sort((a, b) => {
          if (!a.timestamp || !b.timestamp) return 0; // Lida com mensagens sem timestamp
          return new Date(a.timestamp.toDate()) - new Date(b.timestamp.toDate());
        })}
        keyExtractor={(item, index) => index.toString()}
        renderItem={({ item }) => (
          <View
            style={[
              styles.message,
              item.senderId === currentUser ? styles.sent : styles.received,
            ]}
          >
            <Text style={styles.messageText}>{item.message}</Text>
            <Text style={styles.timestampText}>
              {item.timestamp
                ? new Date(
                    item.timestamp.toDate
                      ? item.timestamp.toDate()
                      : item.timestamp
                  ).toLocaleTimeString('pt-BR', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : 'Enviando...'}
            </Text>
          </View>
        )}
        contentContainerStyle={styles.messageList}
      />

      <View style={styles.inputContainer}>
        <TextInput
          style={styles.input}
          placeholder="Digite uma mensagem..."
          placeholderTextColor="#aaa"
          value={message}
          onChangeText={setMessage}
        />
        <TouchableOpacity style={styles.sendButton} onPress={sendMessage}>
          <Text style={styles.sendButtonText}>Enviar</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#2c5364',
    padding: 10,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#2c5364',
  },
  errorText: {
    color: '#FFF',
    fontSize: 18,
  },
  messageList: {
    paddingBottom: 10,
  },
  message: {
    padding: 10,
    marginVertical: 5,
    borderRadius: 10,
    maxWidth: '80%',
  },
  sent: {
    alignSelf: 'flex-end',
    backgroundColor: '#203a43',
  },
  received: {
    alignSelf: 'flex-start',
    backgroundColor: '#203a43',
  },
  messageText: {
    color: '#FFF',
    fontSize: 16,
  },
  timestampText: {
    fontSize: 12,
    color: '#AAA',
    marginTop: 5,
    textAlign: 'right',
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#203a43',
    padding: 10,
    borderRadius: 10,
  },
  input: {
    flex: 1,
    color: '#FFF',
    marginRight: 10,
    padding: 10,
    backgroundColor: '#2c5364',
    borderRadius: 8,
  },
  sendButton: {
    backgroundColor: '#00FF9D',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 10,
  },
  sendButtonText: {
    color: '#203a43',
    fontWeight: 'bold',
    fontSize: 16,
  },
});

export default Chat;
